/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser as userIsAuthenticated } from 'js/lib/user';

import { Button, Grid, breakpoints, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import ActionBar from 'bundles/expression-of-interest/components/ActionBar';
import { EoiDialog } from 'bundles/expression-of-interest/components/EoiDialog';
import { EoiOptimizationsContextProvider } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import { ACTION_QUERY_PARAMS } from 'bundles/expression-of-interest/constants';
import type { ActionBarProps, EoiButtonType } from 'bundles/expression-of-interest/types';
import { buildQualificationSurveyProductId } from 'bundles/expression-of-interest/utils/productId';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

import _t from 'i18n!nls/xddp';

type Props = {
  slug: string;
  degreeId?: string;
  applicationUrl?: string;
  programInterestText?: string;
  enrollButtonData?: EoiButtonType | null;
  emailMeInfoButtonData?: EoiButtonType;
  actionBarData: ActionBarProps;
  isEnrollFlow?: boolean;
  isDegree?: boolean;
};

const styles = {
  container: css`
    background-color: var(--cds-color-neutral-background-primary);
    color: var(--cds-color-neutral-primary);
    box-shadow: 0 2px 4px 0 var(--cds-color-neutral-primary);
    border-top: 12px solid var(--cds-color-emphasis-primary-background-strong);
    position: relative;
    z-index: 5;
    width: 100%;
    padding: var(--cds-spacing-300);

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${typography2.bodyPrimary};
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-pressed);
        }
      }

      h2 {
        ${typography2.subtitleLarge};
      }

      p {
        ${typography2.bodyPrimary};
        margin: 0;
      }
    }
  `,

  content: () => css`
    margin-top: var(--cds-spacing-200);
    width: 100%;
  `,

  button: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  emptyContainer: css`
    ${breakpoints.down('md')} {
      height: 210px;
    }
  `,
};

/**
 * Mimic the dimensions of the EoiWidgetContainer so as not to throw off labels
 */
export const EmptyEoiWidgetContainer = () => <div css={styles.emptyContainer}></div>;

/**
 * This component renders an action bar with buttons for enrolling in a program or requesting more information.
 * It manages the state for the Expression of Interest (EOI) dialog and handles user actions such as clicking the enroll button or the "Email me info" button.
 *
 * @param {string} props.slug - The slug for the program.
 * @param {string} props.degreeId - The ID of the degree program.
 * @param {string} props.applicationUrl - The URL for starting the application process.
 * @param {string} [props.programInterestText] - The text to display in the program interest section.
 * @param {Object} props.actionBarData - Data for configuring the action bar.
 * @param {Object} props.enrollButtonData - Data for configuring the enroll button.
 * @param {Object} props.emailMeInfoButtonData - Data for configuring the "Email me info" button.
 * @param {boolean} props.isEnrollFlow - Indicates whether the component is in enroll flow mode.
 * @param {boolean} props.isDegree - Indicates whether the program is a degree program.
 *
 * @returns {JSX.Element} The rendered EoiWidgetContainer component.
 */
export const EoiWidgetContainer = (props: Props) => {
  const {
    slug,
    degreeId,
    applicationUrl,
    programInterestText,
    actionBarData,
    enrollButtonData,
    emailMeInfoButtonData,
    isEnrollFlow,
    isDegree,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [isEoiOpen, setIsEoiOpen] = useState<boolean>(false);

  // A page whose URL slug is not a registered degree slug resolves no `degreeId`, and an EOI
  // submitted against it is discarded server-side before anything is persisted or emailed. Do
  // not offer a form that cannot submit, and do not collect the learner's details for nothing.
  const qualificationSurveyProductId = buildQualificationSurveyProductId(ProductTypes.DEGREE, degreeId);
  const canSubmitEoi = qualificationSurveyProductId !== null;

  useEffect(() => {
    if (canSubmitEoi) return;

    Sentry.captureMessage('EOI widget rendered without a resolvable degree product', {
      level: 'error',
      extra: { slug, degreeId, pathname: location.pathname },
    });
  }, [canSubmitEoi, slug, degreeId, location.pathname]);

  const programInterestTitle = _t('Learn more about this program');
  let programInterestDescription = '';
  if (!applicationUrl) {
    programInterestDescription = _t('Request more info about this program');
  } else {
    programInterestDescription = _t('Request more info and start your application');
  }

  const programInterestDefault = `## ${programInterestTitle}\n${programInterestDescription}`;

  const handleEmailMeInfoClick = () => {
    setIsEoiOpen(true);
    emailMeInfoButtonData?.doAction?.();
  };

  const handleEnrollButtonClick = () => {
    enrollButtonData?.doAction?.();

    if (enrollButtonData?.isInChecklistMode) {
      setIsEoiOpen(true);
    }

    if (!userIsAuthenticated()) {
      setIsEoiOpen(true);
      const actionQuery = {
        ...location.query,
        action: ACTION_QUERY_PARAMS.ENROLL,
      };
      const params = new URLSearchParams(actionQuery);
      navigate({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    }
  };

  return (
    <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" css={styles.container}>
      <ActionBar actionBarData={actionBarData}>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          container
          direction="row-reverse"
          alignItems="flex-start"
          justifyContent="flex-start"
          spacing={8}
        >
          <Grid item xs={6} md={5}>
            {(applicationUrl || isEnrollFlow) && (
              <Button
                onClick={handleEnrollButtonClick}
                fullWidth
                component={TrackedLink2}
                href={enrollButtonData?.buttonOptions?.htmlAttributes?.href}
                target={enrollButtonData?.buttonOptions?.htmlAttributes?.target}
                rel={enrollButtonData?.buttonOptions?.htmlAttributes?.rel}
                trackingName={enrollButtonData?.buttonOptions?.trackingName}
                withVisibilityTracking={false}
                requireFullyVisible={false}
              >
                {enrollButtonData?.buttonOptions?.label || _t('Enroll')}
              </Button>
            )}
          </Grid>
          <Grid item xs={6} md={5}>
            {canSubmitEoi && (
              <Button
                onClick={handleEmailMeInfoClick}
                fullWidth
                component={TrackedLink2}
                trackingName="email_me_info"
                withVisibilityTracking={false}
                requireFullyVisible={false}
                variant="secondary"
              >
                {emailMeInfoButtonData?.buttonOptions?.label || _t('Email me info')}
              </Button>
            )}
          </Grid>
        </Grid>
      </ActionBar>

      <Grid item>
        <Markdown source={programInterestText || programInterestDefault} />
      </Grid>

      <Grid item css={styles.content}>
        {(applicationUrl || isEnrollFlow) && (
          <Button
            onClick={handleEnrollButtonClick}
            fullWidth
            component={TrackedLink2}
            href={enrollButtonData?.buttonOptions?.htmlAttributes?.href}
            target={enrollButtonData?.buttonOptions?.htmlAttributes?.target}
            rel={enrollButtonData?.buttonOptions?.htmlAttributes?.rel}
            trackingName={enrollButtonData?.buttonOptions?.trackingName}
            withVisibilityTracking={false}
            requireFullyVisible={false}
            css={styles.button}
            data-testid="apply-button"
          >
            {enrollButtonData?.buttonOptions?.label || _t('Enroll')}
          </Button>
        )}
        {canSubmitEoi && (
          <React.Fragment>
            <Button
              onClick={handleEmailMeInfoClick}
              fullWidth
              component={TrackedLink2}
              trackingName="email_me_info"
              withVisibilityTracking={false}
              requireFullyVisible={false}
              variant="secondary"
              css={styles.button}
            >
              {emailMeInfoButtonData?.buttonOptions?.label || _t('Email me info')}
            </Button>

            <EoiOptimizationsContextProvider
              slug={slug}
              productType={isDegree ? ProductTypes.DEGREE : ProductTypes.SPECIALIZATION}
              applyButtonData={enrollButtonData}
            >
              <EoiDialog
                onOpen={() => setIsEoiOpen(true)}
                isOpen={isEoiOpen}
                onClose={() => setIsEoiOpen(false)}
                productId={qualificationSurveyProductId}
                applicationUrl={applicationUrl}
              />
            </EoiOptimizationsContextProvider>
          </React.Fragment>
        )}
      </Grid>
    </Grid>
  );
};

const EoiWidgetContainerWithErrorBoundary = (props: Props) => (
  <ErrorBoundaryWithTagging componentName="expression-of-interest/EoiWidgetContainer">
    <EoiWidgetContainer {...props} />
  </ErrorBoundaryWithTagging>
);

export default EoiWidgetContainerWithErrorBoundary;
